/**
 * Port TypeScript di ../../spec/reference/schedule_engine.py (spec_version 1).
 *
 * Funzioni pure: dipendono solo dagli argomenti. Gli istanti sono millisecondi epoch
 * (oppure ISO8601 con offset, accettati in ingresso); data/ora del palinsesto sono
 * wall-clock nel fuso `manifest.timezone`, mai quello del device.
 *
 *   activeScheduleIds(manifest, at)   regola A
 *   nextItem(manifest, at, current)   regola B (riproduzione libera)
 *   timelineAt(manifest, at)          regola C (gruppi sincronizzati)
 *   nextActiveSetChange(manifest, t)  estensione: prossimo istante in cui cambia l'insieme attivo
 */
import type {
  ContentItem, CurrentRef, FallbackItem, Instant, Manifest, ManifestAsset, NextResult,
  ScheduleEntry, TimelineItem, TimelineResult,
} from '../types';
import {
  MS_DAY, localDay, localToInstant, mondayOf, parseDay, parseInstant, parseSeconds, weekday,
} from './tz';

export const DEFAULT_IMAGE_DISPLAY_MS = 10_000;
const SEGMENT_SCAN_CHUNK_DAYS = 16; // solo ottimizzazione: non cambia il risultato
const FORWARD_SCAN_CHUNKS = 8;      // orizzonte di nextActiveSetChange: 8 * 16 = 128 giorni

// --------------------------------------------------------------------------
// Voci pre-parsate (cache per oggetto: il manifest è immutabile)
// --------------------------------------------------------------------------

interface Parsed {
  startDay: number;
  endDay: number | null;
  startSec: number;
  endSec: number | null;
  anyWeekday: boolean;
}

const parsedCache = new WeakMap<ScheduleEntry, Parsed>();

function parsed(s: ScheduleEntry): Parsed {
  let p = parsedCache.get(s);
  if (!p) {
    p = {
      startDay: parseDay(s.start_date),
      endDay: s.end_date === null ? null : parseDay(s.end_date),
      startSec: parseSeconds(s.start_time),
      endSec: s.end_time === null ? null : parseSeconds(s.end_time),
      anyWeekday: s.weekdays.some(Boolean),
    };
    parsedCache.set(s, p);
  }
  return p;
}

// --------------------------------------------------------------------------
// Regola A: attività
// --------------------------------------------------------------------------

function weekValid(g: number, startDay: number, weekRepeat: number): boolean {
  const w = Math.floor((mondayOf(g) - mondayOf(startDay)) / 7);
  return w >= 0 && w % weekRepeat === 0;
}

/** Il giorno `g` è un giorno di programmazione di `s`? (range date, weekday, settimana). */
function dayOk(s: ScheduleEntry, p: Parsed, g: number): boolean {
  if (g < p.startDay) return false;
  if (p.endDay !== null && g > p.endDay) return false;
  if (!s.weekdays[weekday(g)]) return false;
  return weekValid(g, p.startDay, s.week_repeat);
}

/** Finestra [inizio, fine) in ms epoch della fascia del giorno `g`, oppure null. Solo con end_time. */
function windowOf(s: ScheduleEntry, g: number, tz: string): [number, number] | null {
  const p = parsed(s);
  if (!dayOk(s, p, g)) return null;
  const st = p.startSec;
  const et = p.endSec as number;
  const endDay = et <= st ? g + 1 : g; // <=: scavalca la mezzanotte / 24h
  return [localToInstant(g, st, tz), localToInstant(endDay, et, tz)];
}

function activation(s: ScheduleEntry, tz: string): number {
  const p = parsed(s);
  return localToInstant(p.startDay, p.startSec, tz);
}

function isActive(s: ScheduleEntry, t: number, tz: string): boolean {
  const p = parsed(s);
  if (!p.anyWeekday) return false;
  const d = localDay(t, tz);
  if (p.endSec !== null) {
    for (const g of [d - 1, d]) {
      const w = windowOf(s, g, tz);
      if (w !== null && w[0] <= t && t < w[1]) return true;
    }
    return false;
  }
  // h24 dall'istante di attivazione
  if (t < activation(s, tz)) return false;
  if (p.endDay !== null && d > p.endDay) return false;
  if (!s.weekdays[weekday(d)]) return false;
  return weekValid(d, p.startDay, s.week_repeat);
}

export function activeScheduleIds(manifest: Manifest, at: Instant): string[] {
  const t = parseInstant(at);
  return manifest.schedule.filter((s) => isActive(s, t, manifest.timezone)).map((s) => s.id);
}

// --------------------------------------------------------------------------
// Asset, durate, fallback
// --------------------------------------------------------------------------

type AssetMap = Map<string, ManifestAsset>;

function assetsById(manifest: Manifest): AssetMap {
  const m: AssetMap = new Map();
  for (const a of manifest.assets) m.set(a.id, a);
  return m;
}

export function durationMs(s: ScheduleEntry, asset: ManifestAsset): number {
  let d: number | null | undefined;
  if (asset.kind === 'video') {
    d = asset.duration_ms;
    if (d === null || d === undefined) throw new Error(`video ${asset.id} senza duration_ms`);
  } else {
    d = s.display_ms;
    if (d === null || d === undefined) d = DEFAULT_IMAGE_DISPLAY_MS;
  }
  if (!Number.isInteger(d) || d <= 0) throw new Error(`durata non valida per ${s.id}: ${String(d)}`);
  return d;
}

function fallbackOf(manifest: Manifest, assets: AssetMap): FallbackItem | null {
  const fid = manifest.fallback_asset_id;
  if (fid === null || fid === undefined || !assets.has(fid)) return null;
  return { fallback: true, asset_id: fid };
}

function eligible(manifest: Manifest, t: number, assets: AssetMap): ScheduleEntry[] {
  return manifest.schedule.filter((s) => assets.has(s.asset_id) && isActive(s, t, manifest.timezone));
}

function itemOf(s: ScheduleEntry, playIndex: number, assets: AssetMap): ContentItem {
  return {
    schedule_id: s.id,
    play_index: playIndex,
    asset_id: s.asset_id,
    duration_ms: durationMs(s, assets.get(s.asset_id) as ManifestAsset),
  };
}

// --------------------------------------------------------------------------
// Regola B: riproduzione libera
// --------------------------------------------------------------------------

export function nextItem(manifest: Manifest, at: Instant, current: CurrentRef | null): NextResult {
  const t = parseInstant(at);
  const assets = assetsById(manifest);
  const a = eligible(manifest, t, assets);
  if (a.length === 0) return fallbackOf(manifest, assets);

  const curId = current ? current.schedule_id : null;
  const curIdx = current ? current.play_index : 0;

  // 3. ripetizione della stessa voce
  for (const s of a) {
    if (s.id === curId && curIdx + 1 < s.repeat) return itemOf(s, curIdx + 1, assets);
  }

  // 4. prima voce attiva DOPO `current` nell'array del manifest, altrimenti loop
  const index = new Map<string, number>();
  manifest.schedule.forEach((s, i) => { if (!index.has(s.id)) index.set(s.id, i); });
  if (curId !== null && index.has(curId)) {
    const pos = index.get(curId) as number;
    for (const s of a) {
      if ((index.get(s.id) as number) > pos) return itemOf(s, 0, assets);
    }
  }
  return itemOf(a[0] as ScheduleEntry, 0, assets);
}

// --------------------------------------------------------------------------
// Regola C: riproduzione sincronizzata
// --------------------------------------------------------------------------

function activeSet(manifest: Manifest, t: number, assets: AssetMap): string {
  return eligible(manifest, t, assets).map((s) => s.id).join('\u0000');
}

/** Istanti in cui l'insieme attivo PUÒ cambiare, con data locale in [lo, hi] (numeri di giorno). */
function candidates(manifest: Manifest, lo: number, hi: number): number[] {
  const tz = manifest.timezone;
  const out = new Set<number>();
  for (let g = lo - 1; g <= hi; g++) {
    for (const s of manifest.schedule) {
      if (parsed(s).endSec !== null) {
        const w = windowOf(s, g, tz);
        if (w !== null) { out.add(w[0]); out.add(w[1]); }
      }
    }
  }
  for (const s of manifest.schedule) {
    if (parsed(s).endSec === null) out.add(activation(s, tz));
  }
  for (let g = lo; g <= hi; g++) out.add(localToInstant(g, 0, tz));
  return [...out].filter((c) => {
    const d = localDay(c, tz);
    return d >= lo && d <= hi;
  });
}

/** Inizio del segmento che contiene `t`, mai prima di sync_group.anchor. */
export function segmentStart(manifest: Manifest, t: number, assets: AssetMap): number {
  const tz = manifest.timezone;
  const anchor = parseInstant((manifest.sync_group as { anchor: string }).anchor);
  const anchorDay = localDay(anchor, tz);
  let hi = localDay(t, tz);
  for (;;) {
    const lo = hi - (SEGMENT_SCAN_CHUNK_DAYS - 1);
    const cands = candidates(manifest, lo, hi).filter((c) => c <= t).sort((x, y) => y - x);
    for (const c of cands) {
      if (activeSet(manifest, c, assets) !== activeSet(manifest, c - 1, assets)) return Math.max(c, anchor);
    }
    if (lo <= anchorDay) return anchor;
    hi = lo - 1;
  }
}

export function timelineAt(manifest: Manifest, at: Instant): TimelineResult {
  if (!manifest.sync_group) throw new Error('timelineAt richiede sync_group');
  const t = parseInstant(at);
  const assets = assetsById(manifest);
  const a = eligible(manifest, t, assets);
  if (a.length === 0) return fallbackOf(manifest, assets);

  const start = segmentStart(manifest, t, assets);

  const cycle: Array<{ s: ScheduleEntry; k: number; d: number }> = [];
  for (const s of a) {
    const d = durationMs(s, assets.get(s.asset_id) as ManifestAsset);
    for (let k = 0; k < s.repeat; k++) cycle.push({ s, k, d });
  }
  const total = cycle.reduce((acc, e) => acc + e.d, 0);

  let pos = (((t - start) % total) + total) % total; // modulo matematico: mai negativo
  for (const { s, k, d } of cycle) {
    if (pos < d) {
      const item: TimelineItem = {
        schedule_id: s.id,
        play_index: k,
        asset_id: s.asset_id,
        offset_ms: pos,
        duration_ms: d,
      };
      return item;
    }
    pos -= d;
  }
  throw new Error('irraggiungibile');
}

/**
 * Primo istante c > t in cui l'insieme attivo cambia (A(c) != A(c-1ms)), oppure null se
 * non cambia entro ~128 giorni. Serve al player per sapere quando tagliare in sync e
 * quando rivalutare il palinsesto con schermo nero o fallback fermo.
 */
export function nextActiveSetChange(manifest: Manifest, t: number): number | null {
  const tz = manifest.timezone;
  const assets = assetsById(manifest);
  let lo = localDay(t, tz);
  for (let chunk = 0; chunk < FORWARD_SCAN_CHUNKS; chunk++) {
    const hi = lo + SEGMENT_SCAN_CHUNK_DAYS - 1;
    const cands = candidates(manifest, lo, hi).filter((c) => c > t).sort((x, y) => x - y);
    for (const c of cands) {
      if (activeSet(manifest, c, assets) !== activeSet(manifest, c - 1, assets)) return c;
    }
    lo = hi + 1;
  }
  return null;
}

export { MS_DAY };
